<template>
  <!-- Em abas (ver o `PropertyTabs`); em cada uma, o esqueleto comum (`PropertyFormLayout`) -->
  <ontos-property-tabs ref="tabs" :validation="canFilterOptions || canHaveRules" :invalid="invalidTabs">
    <template #property>
      <!-- Na Propriedade, o que o atributo É à esquerda — identidade e tipo —, o comportamento à direita, e o
           cálculo embaixo, na largura toda, quando ele é calculado -->
      <ontos-property-form-layout>
        <template #main>
          <ontos-property-form-section :title="$tt('section.identity')">
            <div class="property-form-row">
              <!-- Em seleção o nome nasce do domínio, como o do relacionamento nasce da coleção de destino:
                   na maioria das vezes o atributo se chama como o conjunto que ele aceita. Digitar no campo
                   o torna manual, e o botão de recarregar volta ao automático — é o ComputedInput, o mesmo
                   dos alias. Nos demais tipos, campo livre: não há de onde derivar, e um ComputedInput com
                   fonte vazia apagaria o nome ao trocar o tipo. -->
              <ontos-computed-input v-if="model.type === 'choice'"
                                    :label="$tt('attribute-name')" class="required unique" hide-details="auto"
                                    :value="model.name"
                                    :manual="manualName" @input:manual="manualName = $event"
                                    :source="model.domain" :compute="computeName"
                                    :error-messages="errorMessages.name"
                                    @input="model.name = $event; validateName($event)" @blur="validateName()"/>
              <v-text-field v-else class="required unique" :label="$tt('attribute-name')" v-model="model.name"
                            hide-details="auto" :error-messages="errorMessages.name"
                            @update:model-value="validateName($event)" @blur="validateName()"/>
              <ontos-alias-fields :alias="model.alias" :source="model.name" :fixed="fixedAlias"
                                  v-model:manual="manualAlias" :error-message="errorMessages.alias"
                                  @input="model.alias = $event; validateAlias($event)" @blur="validateAlias()"/>
            </div>
          </ontos-property-form-section>

          <ontos-property-form-section :title="$tt('section.type')">
            <div class="property-form-row">
              <ontos-select :items="types" item-text="name" item-value="alias" :label="$tt('attribute-type')"
                            class="required" hide-details="auto" v-model="model.type"/>
              <ontos-select v-if="model.type && typesObj[model.type].subtypes"
                            :items="$arrays.arrayOf(typesObj[model.type].subtypes, 'alias')" item-text="name"
                            item-value="alias" hide-details="auto"
                            :label="$tt('attribute-subtype', {type: typesObj[model.type].name})" class="required"
                            v-model="model.subtype"/>
            </div>
            <!-- Na seleção, o conjunto de valores que o atributo aceita: o domínio e as opções juntos, que
                 são UMA coisa na cabeça de quem configura. Gerir as opções daqui existe porque é configurando
                 o atributo que se percebe que falta um valor ou que a ordem não é a que se quer — e isso
                 obrigava a ir ao design do ambiente. A lista é a MESMA do editor de domínio, pelo
                 ColoredEntryList. Com qual começa é o valor padrão, no Comportamento, como em todo tipo. -->
            <template v-if="model.type === 'choice'">
              <!-- Escolhe entre os domínios existentes ou cria um com o nome digitado, como o campo de
                   relacionamento do formulário de item faz com os itens -->
              <ontos-creatable-autocomplete :items="workspace.domains" item-title="name" item-value="alias"
                                            class="required domain-field" :label="$tt('domain')" hide-details="auto"
                                            :model-value="model.domain"
                                            @update:model-value="onDomainPicked($event)"
                                            @create="creatingDomainName = $event"/>
              <!-- :key pelo domínio: a lista lê as entradas UMA vez, no mounted — é o desenho dela, e um
                   @Watch remapeando descartaria erros, chaves de campo e foco a cada eco. Remontar por
                   chave é como o resto da casa recarrega o que lê no mounted. -->
              <ontos-colored-entry-list ref="options" editable reorderable :min="1"
                                        :key="model.domain || 'none'" :resumed="resumedOptions"
                                        :entries="chosenDomain ? chosenDomain.options : []"
                                        :title="$tt('options')" :level="4" dense :divider="false"
                                        :create-label="$tt('option-create')"
                                        :add-disabled="!chosenDomain"
                                        @update:entries="onOptionsChanged($event)"/>
              <!-- Sem "página inteira": sair daqui perderia o atributo que está sendo montado -->
              <ontos-domain-dialog v-if="creatingDomainName !== null"
                                   :workspace="workspace" :initial-name="creatingDomainName"
                                   :saving="savingDomain" :full-page="false"
                                   @create="createDomain($event)" @closed="closeDomainDialog()"/>
            </template>
          </ontos-property-form-section>

        </template>

        <!-- O comportamento na coluna da direita, quando a janela cabe as duas (pedido do usuário) -->
        <template #side>
          <ontos-property-form-section :title="$tt('section.behavior')">
            <ontos-property-behavior :model="model" :original="value" kind="attribute"
                                     @multiple="model.multiple = !!$event"/>
            <!-- Calculado não tem padrão: o valor vem da fórmula, desde o primeiro item. Na seleção, só
                 depois de escolher o domínio: sem ele não há opção a escolher -->
            <ontos-property-default v-if="offersDefault" ref="default" :value="model.default" :draft="formulaDraft"
                                    :bindings="formulaBindings" :options="defaultOptions"
                                    :result-label="formulaResultLabel" @input="model.default = $event"/>
          </ontos-property-form-section>
        </template>

        <!-- Na largura toda, embaixo das duas colunas: a fórmula ganha espaço (escolha do usuário). Sem a
             lista de parâmetros que acompanhava o script de antes: a fórmula diz do que depende pela
             própria escrita, e quem a lê é o servidor — que também a confere enquanto se digita -->
        <template v-if="model.computed" #bottom>
          <ontos-property-form-section :title="$tt('section.computation')">
            <ontos-formula-input ref="formula" class="required" v-bind="formulaBindings" :value="model.formula"
                                 :result-label="formulaResultLabel" :draft="formulaDraft" :example="examples.computed"
                                 @input="model.formula = $event" @validity="formulaValid = $event"/>
            <ontos-rollup-builder :idea="idea" :workspace="workspace" @apply="applyRollup($event)"/>
          </ontos-property-form-section>
        </template>
      </ontos-property-form-layout>
    </template>

    <template #validation>
      <ontos-property-form-layout>
        <template #main>
          <!-- Na seleção: o que se pode escolher, antes das regras, que validam o que se escolheu -->
          <ontos-property-form-section v-if="canFilterOptions" :title="$tt('option-filter')" :explain="$tt('option-filter-explain')">
            <ontos-option-filter-input ref="optionFilter" :bindings="formulaBindings" :draft="formulaDraft"
                                       :example="examples.optionFilter" :value="model.optionFilter" @input="model.optionFilter = $event"
                                       :message="model.optionFilterMessage" @message="model.optionFilterMessage = $event"/>
          </ontos-property-form-section>
          <ontos-property-form-section v-if="canHaveRules" :title="$tt('rules')" :explain="$tt('rules-explain')">
            <ontos-rules-input ref="rules" :bindings="formulaBindings" :draft="formulaDraft" :example="examples.rule"
                               :rules="model.rules" @input="model.rules = $event"/>
          </ontos-property-form-section>
        </template>
      </ontos-property-form-layout>
    </template>

    <!-- "Visível quando" e "Editável quando" (pedido do usuário): o editor de acesso da coleção, com as
         duas perguntas do campo -->
    <template #access>
      <ontos-access-input ref="access" v-bind="fieldAccessBindings({...formulaDraft, identifier: model.identifier},
                              undefined, formulaBindings.current)"
                          :value="model.access || {}" @input="model.access = $event"/>
    </template>
  </ontos-property-tabs>
</template>
<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import Select from "@/components/shared/Select.vue";
import CreatableAutocomplete from "@/components/shared/CreatableAutocomplete.vue";
import {defineAsyncComponent} from "vue";
import Domain from "@/types/Domain";
import types from '@/model/types'
import OntosComputedInput from "@/components/shared/ComputedInput.vue";
import AliasFields from "@/components/design/property/AliasFields.vue";
import RollupBuilder from "@/components/design/property/RollupBuilder.vue";
import {Rollup} from "@/utils/rollup";
import IdeaProperty, {defaultable, defaultValueOf, PropertyDefault, PropertyRule} from "@/types/IdeaProperty";
import PropertyDefaultInput, {writtenDefault} from "@/components/design/property/PropertyDefault.vue";
import {typeLabel} from "@/utils/propertyDefinition";
import {workspaceNames} from "@/utils/workspaceNames";
import {multipleLabel} from "@/utils/formulaCompletion";
import PropertyInput, {accessValue} from "@/components/design/property/PropertyInput";
import {PropertyAccess} from "@/types/Idea";
import arrayUtils from "@/utils/arrayUtils";
import {isNullOrUndefined} from "@/utils/utils";
import ColoredEntryList from "@/components/shared/ColoredEntryList.vue";
import PropertyFormLayout from "@/components/design/property/PropertyFormLayout.vue";
import PropertyFormSection from "@/components/design/property/PropertyFormSection.vue";
import PropertyBehavior, {behaviorModelOf, behaviorValue} from "@/components/design/property/PropertyBehavior.vue";
import {deepStructuredDiff} from "@/utils/modelUtils";
import {FormulaExamples, formulaExamples} from "@/utils/formulaExamples";
import {domainErrorMessage} from "@/utils/domainErrors";
import OntosError from "@/model/OntosError";
import {EditableEntry} from "@/components/shared/ColoredEntryList.vue";
import {deepCopy} from "@/utils/copy";


function strong(val: any) {
  return val || undefined
}

/** O que o atributo leva ao expandir, além do rascunho — ver o `resume`. */
type AttributeResume = {
  optionsDraft: any[] | null, optionRows: EditableEntry[] | null,
  manualName: boolean, manualAlias: boolean, computed: boolean
}

@Component({
  emits: ['input'],
  components: {
    'ontos-computed-input': OntosComputedInput,
    'ontos-alias-fields': AliasFields,
    'ontos-select': Select,
    'ontos-creatable-autocomplete': CreatableAutocomplete,
    'ontos-property-default': PropertyDefaultInput,
    'ontos-colored-entry-list': ColoredEntryList,
    'ontos-property-form-layout': PropertyFormLayout,
    'ontos-property-form-section': PropertyFormSection,
    'ontos-property-behavior': PropertyBehavior,
    //assíncrono: o editor de domínio só é carregado se alguém pedir para criar um
    'ontos-domain-dialog': defineAsyncComponent(() => import('@/components/design/domain/DomainDialog.vue')),
    //O editor de fórmula vem da base (PropertyInput), assíncrono também
    'ontos-rollup-builder': RollupBuilder
  }
})
class AttributeInput extends PropertyInput {
  /**
   * O nome com que o domínio novo começa — o digitado no campo de domínio, ou vazio pelo "+" sem texto;
   * null = modal fechado. Null, e não vazio, porque o "+" abre a janela também sem nome.
   */
  creatingDomainName: string | null = null
  savingDomain = false

  typesObj = types
  types = arrayUtils.arrayOf(types, 'alias')
  manualAlias = false
  manualName = false
  model = {
    name: "",
    alias: "",
    type: "",
    subtype: "",
    multiple: false,
    required: false,
    unique: false,
    computed: false,
    formula: "",
    minItems: 0 as number | string,
    maxItems: null as number | string | null,
    identifier: false,
    domain: "",
    default: null as PropertyDefault | null,
    rules: [] as PropertyRule[],
    optionFilter: undefined as string | undefined,
    optionFilterMessage: undefined as string | undefined,
    access: undefined as PropertyAccess | undefined
  }

  mounted() {
    //Atributo que já tem nome não é renomeado sozinho ao trocar o domínio: quem o
    //nomeou foi alguém, mesmo que por acaso o nome coincida com o do domínio.
    if (this.value.name) this.manualName = true
    this.model = {...this.model, ...this.value} as any
    //`computed` é só uma flag de tela: o que se persiste é a fórmula. Sem derivá-la
    //aqui, reabrir um atributo calculado a trazia falsa, a linha da fórmula não
    //renderizava e o currentValue emitia formula: undefined — editar apagava a fórmula.
    this.model.computed = !!this.value.formula
    this.formulaValid = !!this.value.formula
    Object.assign(this.model, behaviorModelOf(this.value))
    if (this.resumed) this.resume(this.resumed)
    this.$watch('model', () => {
      this.$emit('input', this.currentValue);
    }, { deep: true });
  }

  /**
   * O que a janela guardava e o rascunho não diz (ver o `snapshot`), ao expandir: as opções do domínio
   * mexidas — as linhas da lista como estavam —, o nome e o identificador digitados à mão (sem isso,
   * mudar o nome recalcularia o identificador digitado) e o "Calculado" ligado ainda sem fórmula, que o
   * rascunho não guarda.
   */
  resume(resumed: AttributeResume) {
    this.optionsDraft = resumed.optionsDraft
    this.resumedOptions = resumed.optionRows
    this.manualName = resumed.manualName
    this.manualAlias = resumed.manualAlias
    this.model.computed = resumed.computed
  }

  snapshot(): AttributeResume {
    const list = this.$refs.options as { rows: EditableEntry[] } | undefined
    return {optionsDraft: this.optionsDraft && deepCopy(this.optionsDraft), optionRows: list ? deepCopy(list.rows) : null,
      manualName: this.manualName, manualAlias: this.manualAlias, computed: this.model.computed}
  }

  /** As linhas da lista de opções que a janela deixou, para a primeira montagem dela — ver o `resume`. */
  resumedOptions: EditableEntry[] | null = null

  /**
   * Grava pelo mesmo caminho do design do ambiente, e já deixa o domínio escolhido.
   *
   * A recusa vai à barra de mensagem, como a das opções (ver `commit`); o modal fica aberto, que é
   * onde se pode corrigir.
   */
  async createDomain(domain: Domain) {
    this.savingDomain = true
    try {
      const created = await this.$domains.addTo(this.workspace, domain)
      this.onDomainPicked(created.alias)
      this.closeDomainDialog()
    } catch (err) {
      throw new OntosError(this.$tt('error.create-domain', {name: domain.name}) as string, err as Error)
    } finally {
      this.savingDomain = false
    }
  }

  closeDomainDialog() {
    this.creatingDomainName = null
  }

  /**
   * O atributo como a checagem da fórmula precisa vê-lo: o tipo e o múltiplo dizem o que serve, e o
   * alias — na criação, o que está sendo digitado — é o que deixa acusar a fórmula que cita a si.
   */
  get formulaDraft() {
    return {name: this.model.name, alias: strong(this.model.alias),
      type: strong(this.model.type), subtype: strong(this.model.subtype), multiple: strong(this.model.multiple),
      //Na seleção, o servidor troca o ALIAS da opção pelo id por este domínio — sem ele a prévia
      //da calculada de seleção daria opção desconhecida
      domain: this.model.type === 'choice' ? strong(this.model.domain) : undefined}
  }

  /**
   * O tipo do atributo como o card o escreve ("Seleção (Prioridade)", "Número (Decimal)"), com o
   * múltiplo — o que o editor mostra como resultado da fórmula que passou.
   */
  /** Os exemplos embaixo das fórmulas, com os nomes desta coleção — a regra mora no `formulaExamples`. */
  get examples(): FormulaExamples {
    return formulaExamples(this.formulaDraft, this.idea, this.workspace)
  }

  get formulaResultLabel(): string {
    const label = typeLabel({name: '', alias: '', type: this.model.type, subtype: this.model.subtype,
      domain: this.model.domain} as IdeaProperty, workspaceNames(this.workspace))
    return multipleLabel(label, this.model.multiple)
  }

  /** O construtor de rollup montou o atributo: a fórmula e o tipo que ela pede. */
  applyRollup(rollup: Rollup) {
    this.model.type = rollup.type
    this.model.subtype = rollup.subtype || ''
    this.model.multiple = rollup.multiple
    if (rollup.domain) this.model.domain = rollup.domain
    this.model.formula = rollup.formula
  }

  /**
   * Tem valor padrão? Nem o calculado, nem a mídia (`defaultable`); e a seleção, só com o domínio
   * escolhido — sem ele não há opção a escolher.
   */
  get offersDefault(): boolean {
    return !!this.model.type && !this.model.computed && defaultable({type: this.model.type})
        && (this.model.type !== 'choice' || !!this.chosenDomain)
  }

  /** O domínio escolhido, ou undefined — é ele que a seção de opções edita. */
  get chosenDomain(): Domain | undefined {
    return this.model.type === 'choice' ? this.workspace.domainWith(this.model.domain) : undefined
  }

  /** As opções como estão no formulário, ou null enquanto ninguém mexeu. */
  optionsDraft: any[] | null = null

  /** Há o que gravar? Compara com o que está no ambiente, que é o que o servidor tem. */
  get optionsChanged(): boolean {
    if (!this.optionsDraft || !this.chosenDomain) return false
    return Object.keys(deepStructuredDiff(
        {options: this.chosenDomain.options}, {options: this.optionsDraft})).length > 0
  }

  /**
   * As opções mexidas vão junto com o Salvar da PROPRIEDADE — não há um segundo botão.
   *
   * Vai ANTES dela, e abortando o salvar se falhar: o domínio é entidade própria, e a
   * propriedade pode apontar para uma opção dele no valor default. Gravar a propriedade com o
   * domínio recusado deixaria a tela dizendo que salvou o que não salvou.
   *
   * A recusa — remover opção em uso, ou citada numa fórmula — sai como `OntosError`, que a tela de
   * rota acima mostra na barra de mensagem, como todo erro de gravar. Era um aviso dentro da seção,
   * o único da casa ao lado do diálogo de criar domínio, e destoava (observação do usuário).
   */
  async commit(): Promise<boolean> {
    if (!this.optionsChanged) return true
    const domain = this.chosenDomain!
    const list = this.$refs.options as any
    if (!list || !list.validateModel()) return false
    try {
      await this.$domains.updateIn(this.workspace, domain, {options: this.optionsDraft})
      this.optionsDraft = null
      //Reconfere contra o que o servidor devolveu: o rascunho saiu, e a lista em vigor agora é
      //a do ambiente, com os ids que nasceram lá.
      this.dropDanglingDefault()
      return true
    } catch (err) {
      throw new OntosError(domainErrorMessage(err, this.$tt('error.update-domain', {name: domain.name}) as string), err as Error)
    }
  }

  /**
   * A lista mudou: guarda o rascunho e põe o valor padrão de acordo com ele.
   *
   * O default tem de acompanhar a lista AQUI, e não só depois de gravar: removida a opção que
   * era o padrão, o campo abaixo ficava com o id de algo que não existe mais e o v-select
   * desenhava um item fantasma — sem nome e sem cor, que é a bolinha preta. É o mesmo veneno do
   * vínculo vazio, por outro caminho.
   */
  onOptionsChanged(options: any[]) {
    this.optionsDraft = options
    this.dropDanglingDefault()
  }

  /**
   * Tira do valor padrão o que não casa com opção nenhuma da lista em vigor — o rascunho, quando
   * há. Nada quebra com o id pendurado, porque quem semeia e quem escreve o rótulo já o ignoram;
   * o que não se pode é gravá-lo nem mostrá-lo.
   */
  private dropDanglingDefault() {
    const value = defaultValueOf(this.model)
    if (isNullOrUndefined(value)) return
    const options = this.defaultOptions
    const kept = this.$arrays.asArray(value)
        .filter((id: any) => options.some(option => option.id === id))
    this.model.default = {value: !kept.length ? null : this.model.multiple ? kept : kept[0]}
  }

  /**
   * As opções que o campo de valor padrão oferece.
   *
   * **Do RASCUNHO quando há um.** Renomear, recolorir ou remover uma opção tem de aparecer aqui
   * na hora: lia do ambiente, que é o estado do SERVIDOR, e por isso o campo mostrava o antigo
   * enquanto a lista logo acima já mostrava o novo — o mesmo domínio com duas caras na mesma
   * tela, até salvar.
   *
   * **Só as que têm id.** O id nasce no servidor, então opção recém-acrescentada ainda não tem
   * valor que o campo possa guardar: oferecê-la faria escolhê-la gravar um default vazio, em
   * silêncio. Ela entra na lista depois de a propriedade ser salva.
   */
  get defaultOptions() {
    const options = this.optionsDraft || (this.chosenDomain ? this.chosenDomain.options : [])
    return options.filter((option: any) => !isNullOrUndefined(option.id))
  }

  /**
   * Escolhe o domínio — também o recém-criado daqui, e por isso o `createDomain` passa por aqui. O padrão
   * fixo de antes sai pelo `PropertyDefault`, que acompanha a forma do campo: o id passaria a apontar
   * para outro conjunto.
   */
  onDomainPicked(alias: string) {
    this.model.domain = alias
    //O rascunho é das opções DAQUELE domínio: sobrevivendo à troca, o Salvar da propriedade
    //gravaria a lista de um domínio por cima do outro. As linhas que a janela deixou, idem
    this.optionsDraft = null
    this.resumedOptions = null
  }

  /** O nome do domínio escolhido; sem domínio não há o que sugerir. */
  computeName(domainAlias: string): string {
    const domain = this.workspace.domainWith(domainAlias)
    return domain ? domain.name : ''
  }

  get currentValue() {
    return {
      name: strong(this.model.name),
      alias: strong(this.model.alias),
      type: strong(this.model.type),
      subtype: strong(this.model.subtype),
      //Só faz sentido no tipo seleção; em qualquer outro vira undefined, que o
      //updatedProps transforma em null e o servidor em $unset — é como trocar o tipo
      //de um atributo de seleção para texto limpa o domínio que ele apontava.
      domain: this.model.type === 'choice' ? strong(this.model.domain) : undefined,
      //Mesma regra do domain logo acima: sem padrão vira undefined, que o updatedProps transforma em
      //null e o servidor em $unset. Sem padrão é o estado normal, e o campo limpo precisa chegar assim
      //para o designer poder voltar a ele
      default: this.offersDefault ? writtenDefault(this.model.default) : undefined,
      //Múltiplo, obrigatório, exclusivo, mínimo/máximo e identificador: o que a seção Comportamento
      //decide, como vai gravado — o mesmo do relacionamento
      ...behaviorValue(this.model, this.value),
      formula: this.model.computed ? strong(this.model.formula) : undefined,
      rules: this.rulesValue,
      ...this.optionFilterFields,
      access: accessValue(this.model.access)
    }
  }
}

export default toNative(AttributeInput)
</script>

<style scoped>
/* O domínio na largura de um campo, e não da coluna, como o valor padrão (`PropertyDefault`) */
.domain-field {
  max-width: 320px;
}
</style>